import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/FormPage.css';

function CreateClient() {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(Boolean(clientId));
    const [formData, setFormData] = useState({
        name: '',
        panNo: '',
        gstNo: '',
        address: ''
    });

    useEffect(() => {
        if (clientId) {
            withLoading(async () => {
                const res = await api.get(`/clients/${clientId}`);
                setFormData({
                    name: res.data.name || '',
                    panNo: res.data.panNo || '',
                    gstNo: res.data.gstNo || '',
                    address: res.data.address || ''
                });
            }).catch(() => {
                alert('Error fetching client data');
            });
        }
    }, [clientId, withLoading]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const apiCall = clientId
            ? api.put(`/clients/${clientId}`, formData)
            : api.post('/clients', formData);

        apiCall
            .then(() => {
                navigate('/clients');
            })
            .catch(() => {
                alert('Error saving client details');
            });
    };

    return (
        <PageLoadingArea isLoading={isLoading}>
            <div className="form-page">
                <div className="form-page-header">
                    <div>
                        <h1>{clientId ? 'Edit client' : 'Add client'}</h1>
                        <p className="form-page-subtitle">
                            {clientId
                                ? 'Update client name, address, PAN, and GST details'
                                : 'Enter client details to add them to your records'}
                        </p>
                    </div>
                    <div className="form-page-actions">
                        <button
                            type="button"
                            className="form-page-btn-secondary"
                            onClick={() => navigate('/clients')}
                        >
                            Back to list
                        </button>
                        <button
                            type="submit"
                            form="create-client-form"
                            className="form-page-btn"
                        >
                            {clientId ? 'Update client' : 'Save client'}
                        </button>
                    </div>
                </div>

                <section className="form-page-section">
                    <div className="form-page-section-head">
                        <div>
                            <h2>Client details</h2>
                            <p className="form-page-section-hint">Name, address, and tax identifiers</p>
                        </div>
                    </div>

                    <form id="create-client-form" onSubmit={handleSubmit}>
                        <div className="form-page-grid cols-3">
                            <div className="form-page-field span-full">
                                <label htmlFor="name">Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    placeholder="Client or company name"
                                />
                            </div>
                            <div className="form-page-field span-full">
                                <label htmlFor="address">Address (optional)</label>
                                <textarea
                                    id="address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    rows={3}
                                    placeholder="Enter client address"
                                />
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="panNo">PAN No</label>
                                <input
                                    type="text"
                                    id="panNo"
                                    name="panNo"
                                    value={formData.panNo}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="gstNo">GST No</label>
                                <input
                                    type="text"
                                    id="gstNo"
                                    name="gstNo"
                                    value={formData.gstNo}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                    </form>
                </section>
            </div>
        </PageLoadingArea>
    );
}

export default CreateClient;
